import { Box, Typography, useTheme } from "@mui/material";
import { format, isValid } from "date-fns";
import { Calendar } from "lucide-react";
import { useState } from "react";
import { VALUE_LINE_HEIGHT } from "web_myplann/Shared/Components/FlatRow/constants";
import { FlatRowDateDayPanel } from "web_myplann/Shared/Components/FlatRow/FlatRowDateDayPanel";
import { FlatRowDateMonthPanel } from "web_myplann/Shared/Components/FlatRow/FlatRowDateMonthPanel";
import { FlatRowDatePanel } from "web_myplann/Shared/Components/FlatRow/FlatRowDatePanel";
import { FlatRowTriggerButton } from "web_myplann/Shared/Components/FlatRow/FlatRowTriggerButton";
import { FlatRowTriggerValue } from "web_myplann/Shared/Components/FlatRow/FlatRowTriggerValue";
import { MyPlannDrawer } from "web_myplann/Shared/Components/MyPlannDrawer";
import { useDateFnsLocale } from "web_myplann/Shared/Hooks/useDateFnsLocale";
import { useFlatRowRedesign } from "web_myplann/Shared/Hooks/useFlatRowRedesign";
import {
	type DatePanelMode,
	DateView,
} from "web_myplann/Shared/Types/DateView";

export type FlatRowDatePickerProps = {
	label: string;
	value: Date | null;
	mode: DatePanelMode;
	displayFormat: string;
	placeholder?: string;
	minDate?: Date;
	maxDate?: Date;
	onChange: (date: Date) => void;
	onBlur?: () => void;
	drawerTitle?: string;
	isLast?: boolean;
	disabled?: boolean;
	error?: boolean;
	helperText?: string;
};

export const FlatRowDatePicker = ({
	label,
	value,
	mode,
	displayFormat,
	placeholder,
	minDate,
	maxDate,
	onChange,
	onBlur,
	drawerTitle,
	isLast,
	disabled,
	error,
	helperText,
}: FlatRowDatePickerProps) => {
	const theme = useTheme();
	const locale = useDateFnsLocale();
	const isRedesign = useFlatRowRedesign();
	const [open, setOpen] = useState(false);

	const displayValue =
		value !== null && isValid(value)
			? format(value, displayFormat, { locale })
			: "";

	const handleSelect = (date: Date) => {
		onChange(date);
		setOpen(false);
		onBlur?.();
	};

	const openDrawer = () => setOpen(true);

	const closeDrawer = () => {
		setOpen(false);
		onBlur?.();
	};

	return (
		<Box>
			<FlatRowTriggerButton
				onClick={openDrawer}
				disabled={disabled}
				sx={{
					borderBottom: isLast
						? "none"
						: `1px solid ${theme.palette.surface.border}`,
				}}
			>
				<Typography
					variant="body2"
					color={error ? theme.palette.error.main : theme.palette.grey[500]}
					flexShrink={0}
					mr={2}
					lineHeight={`${VALUE_LINE_HEIGHT}px`}
				>
					{label}
				</Typography>
				<FlatRowTriggerValue
					value={displayValue}
					placeholder={placeholder}
					icon={Calendar}
					capitalize
					disabled={disabled}
				/>
			</FlatRowTriggerButton>

			{error && helperText && (
				<Typography
					component="p"
					variant="caption"
					color={theme.palette.error.main}
					px={theme.spacingTokens.lg}
					pb={0.6}
				>
					{helperText}
				</Typography>
			)}

			<MyPlannDrawer
				anchor="bottom"
				open={open}
				onClose={closeDrawer}
				title={drawerTitle ?? label}
				grabber
			>
				{isRedesign && (
					<Box
						pb={`calc(${theme.datePanel.paddingBottom} + ${theme.bottomSpacing.sheet})`}
					>
						<FlatRowDatePanel
							value={value}
							mode={mode}
							minDate={minDate}
							maxDate={maxDate}
							onSelect={handleSelect}
						/>
					</Box>
				)}
				{!isRedesign && mode === DateView.Day && (
					<FlatRowDateDayPanel
						value={value}
						minDate={minDate}
						maxDate={maxDate}
						onSelect={handleSelect}
					/>
				)}
				{!isRedesign && mode === DateView.Month && (
					<FlatRowDateMonthPanel
						value={value}
						minDate={minDate}
						maxDate={maxDate}
						onSelect={handleSelect}
					/>
				)}
			</MyPlannDrawer>
		</Box>
	);
};
